import { type RootState } from "features/Application/globaltypes/redux";
import { createSelector } from "@reduxjs/toolkit";
import {
  type EmailTemplateFormFields,
  type CampaignFormFields,
  type LandingPageFormFields,
  type FeedbackPageFormFields,
} from "../Details/types";
import { type TagsPayloadCamelPlural } from "interfaces/Tags";
import { mapTagsToView } from "features/Library/Common/helpers/tagsHelper";

export const campaignsStateSelector = (state: RootState) => state.library.campaigns;

export const campaignFormFieldsSelector = createSelector(
  [(state: RootState) => campaignsStateSelector(state)],
  (value): CampaignFormFields => ({
    id: value.base.entityState.entityId,
    name: value.base.details.campaign.name.trim(),
    campaignTypeId: value.base.details.campaign.campaignTypeId,
    subDomainId: value.base.details.campaign.subDomainId,
    labels: value.base.details.campaign.labels,
    remedialTraining: {
      triggerTypeId: value.base.details.campaign.remedialTraining.triggerTypeId,
      daysToComplete: value.base.details.campaign.remedialTraining.daysToComplete,
      fixedDueDate: value.base.details.campaign.remedialTraining.dueDate,
      isFixedDueDate: Boolean(value.base.details.campaign.remedialTraining.dueDate),
      priority: value.base.details.campaign.remedialTraining.priority,
      flowId: value.base.details.campaign.remedialTraining.flowId,
      flowTitle: value.base.details.campaign.remedialTraining.flowTitle,
      thumbnailUrl: value.base.details.campaign.remedialTraining.flowThumbnailUrl,
    },
  }),
);

export const normalizedTagsSelector = createSelector(
  [(state: RootState): TagsPayloadCamelPlural => state.library.campaigns.tags.tags],
  mapTagsToView,
);

export const emailTemplateFormFieldsSelector = createSelector(
  [(state: RootState) => campaignsStateSelector(state).base.emailTemplate],
  (value): EmailTemplateFormFields => ({
    subject: value.emailTemplate.subject.trim(),
    previewText: value.emailTemplate.previewText.trim(),
    senderName: value.emailTemplate.senderName.trim(),
    senderEmail: value.emailTemplate.senderEmail.trim(),
    html: value.emailTemplate.html.trim(),
    contentUrlWithSas: value.emailTemplate.contentUrlWithSas,
  }),
);

export const landingPageFormFieldsSelector = createSelector(
  [(state: RootState) => campaignsStateSelector(state).base.landingPage],
  (value): LandingPageFormFields => ({
    html: value.landingPage.html.trim(),
    contentUrlWithSas: value.landingPage.contentUrlWithSas,
  }),
);

export const feedbackPageFormFieldsSelector = createSelector(
  [(state: RootState) => campaignsStateSelector(state).base.feedbackPage],
  (value): FeedbackPageFormFields => ({
    videoUrl: value.feedbackPage.videoUrl,
    uploadedVideos: value.feedbackPage.uploadedVideos,
    message: value.feedbackPage.message.trim(),
  }),
);

export const campaignValidationSelector = (state: RootState) => state.library.campaigns.base.campaignValidation;

export const campaignAppliedFilterSelector = (state: RootState) =>
  campaignsStateSelector(state).overview.filters.appliedFilter;

export const campaignSearchSelector = (state: RootState) => campaignsStateSelector(state).overview.filters.search;

export const campaignPaginationSelector = (state: RootState) =>
  campaignsStateSelector(state).overview.filters.pagination;

export const campaignSortingColumnNameSelector = (state: RootState) =>
  state.library.campaigns.overview.filters.sortingColumnName;

export const campaignSortingDirectionSelector = (state: RootState) =>
  state.library.campaigns.overview.filters.sortingDirection;
